// CrmImportModal — bulk contact/company upload from CSV or Excel.
// Ported from the classic Prospects.js import flow (the number-one system):
// file parse (papaparse + SheetJS) -> dry-run preview -> dedup strategy -> import.
// Self-contained so it drops into CRM Contacts and Companies alike.
import { useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import API_BASE from '../../config';

// Column mapper — ported verbatim from classic Prospects.js so uploads behave
// identically (Apollo exports, common header variants).
export function mapRow(row) {
  const get = (...keys) => {
    for (const k of keys) {
      const found = Object.keys(row).find(rk => rk.trim().toLowerCase() === k.trim().toLowerCase());
      if (found && row[found] !== undefined && row[found] !== null && String(row[found]).trim() !== '') return String(row[found]).trim();
    }
    return '';
  };
  const first = get('First Name', 'Contact First Name', 'FirstName');
  const last = get('Last Name', 'Contact Last Name', 'LastName');
  return {
    company_name: get('Company Name', 'Company Name for Emails', 'Company', 'Account', 'Organisation', 'Organization'),
    website: get('Website', 'Company Website', 'Domain'),
    sector: get('Sector', 'Industry', 'Company Industry'),
    size: get('Number of Employees', 'Employees', 'Company Size', 'HeadCount'),
    country: get('Country', 'Company Country', 'Location', 'HQ Country'),
    company_linkedin_url: get('Company Linkedin Url', 'Company LinkedIn', 'LinkedIn Company'),
    keywords: get('Keywords', 'Tags', 'Company Keywords'),
    revenue: get('Revenue', 'Annual Revenue', 'Company Revenue'),
    full_name: [first, last].filter(Boolean).join(' ') || get('Name', 'Full Name', 'Contact Name'),
    job_title: get('Title', 'Job Title', 'Position', 'Role'),
    email: get('Email', 'Email Address', 'Work Email', 'Contact Email'),
    linkedin_url: get('Person Linkedin Url', 'LinkedIn Url', 'LinkedIn', 'Contact LinkedIn', 'LinkedIn URL'),
    phone: get('Phone', 'Phone Number', 'Mobile Phone', 'Direct Phone'),
    notes: get('Notes', 'Comments', 'Description')
  };
}

const STRATEGIES = [
  { key: 'skip', label: 'Skip duplicates', sub: 'Leave existing contacts untouched. Only insert new ones.' },
  { key: 'fill', label: 'Fill empty fields', sub: 'For matched contacts, fill in fields where the existing value is empty.' },
  { key: 'overwrite', label: 'Overwrite with file', sub: 'For matched contacts, replace existing values with file values (where the file has data).' }
];

export default function CrmImportModal({ clientId, onClose, onComplete }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);     // dry-run result
  const [strategy, setStrategy] = useState('skip');
  const [result, setResult] = useState(null);       // final result
  const [error, setError] = useState(null);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setError(null); setResult(null); setPreview(null);
    setFileName(file.name);
    if (file.name.toLowerCase().endsWith('.csv')) {
      Papa.parse(file, { header: true, skipEmptyLines: true, complete: r => setRows(r.data.map(mapRow).filter(x => x.company_name || x.full_name)) });
    } else {
      const reader = new FileReader();
      reader.onload = ev => {
        const wb = XLSX.read(ev.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        setRows(data.map(mapRow).filter(x => x.company_name || x.full_name));
      };
      reader.readAsBinaryString(file);
    }
  }

  async function runPreview() {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows, dry_run: true })
      });
      if (res.status === 402) { setError('This import would exceed your contact limit.'); setBusy(false); return; }
      if (res.status === 413) { setError('The file is too large for the server to accept. Split it and import in parts.'); setBusy(false); return; }
      if (!res.ok) throw new Error(await res.text());
      setPreview(await res.json());
      setStrategy('skip');
    } catch (e) { setError('Preview failed: ' + e.message); }
    setBusy(false);
  }

  async function runImport() {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows, dedup_strategy: strategy })
      });
      if (res.status === 402) { setError('This import would exceed your contact limit.'); setBusy(false); return; }
      if (res.status === 413) { setError('The file is too large for the server to accept. Split it and import in parts.'); setBusy(false); return; }
      if (!res.ok) throw new Error(await res.text());
      const r = await res.json();
      setResult(r);
      setPreview(null);
      if (onComplete) onComplete(r);
    } catch (e) { setError('Import failed: ' + e.message); }
    setBusy(false);
  }

  const dupCount = preview ? ((preview.matchBreakdown?.byEmail || 0) + (preview.matchBreakdown?.byLinkedin || 0) + (preview.matchBreakdown?.byName || 0)) : 0;

  return (
    <div className="crmimp-overlay" onClick={() => !busy && onClose()}>
      <div className="crmimp" onClick={e => e.stopPropagation()}>
        {/* STEP 1 — file pick (hidden once preview/result shown) */}
        {!preview && !result && (
          <>
            <div className="crmimp-head">
              <h2 className="crmimp-title">Import contacts</h2>
              <button className="crmimp-x" onClick={onClose} title="Close">×</button>
            </div>
            <p className="crmimp-sub">Upload a CSV or Excel file. Apollo exports and common column names are mapped automatically.</p>

            <label className="crmimp-drop">
              <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} hidden />
              <div className="crmimp-drop-ic">↑</div>
              <div className="crmimp-drop-main">{fileName || 'Choose a CSV or Excel file'}</div>
              <div className="crmimp-drop-sub">.csv, .xlsx, .xls</div>
            </label>

            {rows.length > 0 && <p className="crmimp-count">{rows.length} row{rows.length === 1 ? '' : 's'} parsed and ready.</p>}
            {error && <p className="crmimp-err">{error}</p>}

            <div className="crmimp-actions">
              <button className="crmimp-btn crmimp-btn--ghost" onClick={onClose}>Cancel</button>
              <button className="crmimp-btn crmimp-btn--primary" disabled={busy || rows.length === 0} onClick={runPreview}>
                {busy ? 'Checking...' : `Preview import (${rows.length})`}
              </button>
            </div>
          </>
        )}

        {/* STEP 2 — review / dedup strategy */}
        {preview && !result && (
          <>
            <div className="crmimp-head">
              <h2 className="crmimp-title">Review import</h2>
              <button className="crmimp-x" onClick={() => setPreview(null)} title="Back">×</button>
            </div>
            <p className="crmimp-sub">We found {dupCount} duplicate{dupCount === 1 ? '' : 's'} in this file.</p>

            <div className="crmimp-stats">
              <div className="crmimp-stat"><span>New companies</span><strong>{preview.companiesCreated || 0}</strong></div>
              <div className="crmimp-stat"><span>New contacts</span><strong>{preview.contactsCreated || 0}</strong></div>
              <div className="crmimp-stat"><span>Duplicate contacts found</span><strong>{preview.contactsSkipped || 0}</strong></div>
              {dupCount > 0 && (
                <div className="crmimp-matchline">
                  Matched by email: {preview.matchBreakdown?.byEmail || 0} · LinkedIn: {preview.matchBreakdown?.byLinkedin || 0} · name: {preview.matchBreakdown?.byName || 0}
                </div>
              )}
            </div>

            <p className="crmimp-q">How should duplicates be handled?</p>
            <div className="crmimp-strats">
              {STRATEGIES.map(opt => (
                <label key={opt.key} className={'crmimp-strat' + (strategy === opt.key ? ' is-on' : '')}>
                  <input type="radio" name="dedup" value={opt.key} checked={strategy === opt.key} onChange={() => setStrategy(opt.key)} />
                  <div>
                    <div className="crmimp-strat-l">{opt.label}</div>
                    <div className="crmimp-strat-s">{opt.sub}</div>
                  </div>
                </label>
              ))}
            </div>

            {error && <p className="crmimp-err">{error}</p>}
            <div className="crmimp-actions">
              <button className="crmimp-btn crmimp-btn--ghost" disabled={busy} onClick={() => setPreview(null)}>Back</button>
              <button className="crmimp-btn crmimp-btn--primary" disabled={busy} onClick={runImport}>
                {busy ? `Importing ${rows.length.toLocaleString('en-GB')} rows…` : 'Confirm import'}
              </button>
            </div>
          </>
        )}

        {/* STEP 3 — done */}
        {result && (
          <>
            <div className="crmimp-head">
              <h2 className="crmimp-title">Import complete</h2>
              <button className="crmimp-x" onClick={onClose} title="Close">×</button>
            </div>
            <div className="crmimp-done">
              <div className="crmimp-done-row">Companies: <strong>{result.companiesCreated}</strong> created, {result.companiesSkipped || 0} skipped</div>
              <div className="crmimp-done-row">Contacts: <strong>{result.contactsCreated}</strong> created, {result.contactsUpdated || 0} updated, {result.contactsSkipped || 0} skipped</div>
              {result.errors && result.errors.length > 0 && (
                <div className="crmimp-done-err">
                  {result.errors.length} row{result.errors.length === 1 ? '' : 's'} could not be saved. Everything else was imported.
                  <ul className="crmimp-errlist">
                    {result.errors.slice(0, 10).map((er, i) => <li key={i}><b>{er.row || 'Row'}</b>: {String(er.error || '').slice(0, 140)}</li>)}
                    {result.errors.length > 10 && <li>and {result.errors.length - 10} more</li>}
                  </ul>
                </div>
              )}
            </div>
            <div className="crmimp-actions">
              <button className="crmimp-btn crmimp-btn--primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
